import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import Router from 'next/router'
import toast from 'react-hot-toast'
import Link from 'next/link'
import { useSession } from 'next-auth/react'
import dynamic from 'next/dynamic'
import {
  Work, SRT, Feature, Track, Exhibition
} from '@prisma/client'
import { useForm, Controller, useFieldArray } from 'react-hook-form'
import {
  WorkBacklinkHelp,
  WorkDilationHelp,
  WorkTitleHelp,
  WorkDurationHelp,
  WorkSRTShortDescriptorHelp,
  WorkAudioUploaderHelper,
  WorkAudioUrlHelp,
  AutoTranslationHelper,
  WorkPublishedHelp
} from '@src/components/popovers'
import { AudioUploader } from '@src/components/AudioUploader'
import { TrackEditor } from '@src/components/TrackEditor'
import {
  createWork, updateWork, deleteWork, deleteSrt, useExhibitions, getLastCaptionTime, resetTimestamp, useTenantFeatures, useAvailableLanguages
} from '@src/lib/apiClient'
import { isQuillEmpty, getQuillPlaintext, titleToFilename } from '@src/lib/quill'
import { AdminUiContext } from '@src/context/AdminUiContext'
import { formatTimestamp, convertMS } from '@src/lib/monitoring'
import { WorkWithParsedSrts, ParsedSrt } from '@src/pages/api/work/[id]/index'
import { LanguageCode } from 'deepl-node'
import { ErrorMessage } from '@hookform/error-message'
import { useDebouncedCallback } from '@src/lib/useDebounce'
import { DownloadableQRCode, getOrigin } from './DownloadableQRCode'
import { logger } from '../lib/logger'

const ReactQuill = dynamic(() => import('react-quill'), { ssr: false })

type WorkFormProps = {
  work?: WorkWithParsedSrts;
  mutateWork?: () => void;
}

export type WorkFormValues = {
  title: string;
  exhibitionId: number;
  parsedSrts?: ParsedSrt[];
  id: number;
  backlinkUrl: string;
  srt: string;
  audioUrl: string;
  isActive: boolean;
  duration: number;
  srts: SRT[];
  tracks: Track[];
  lastTimestamp: bigint;
  dilation: number;
  nudge: number;
  sortOrder: number;
  loopingWork: boolean;
  tenantId: number;
  exhibition: Exhibition;
  autoTranslationLanguages: LanguageCode[];
}

export const WorkForm: React.FC<WorkFormProps> = ({ work, mutateWork }: WorkFormProps) => {
  const { data: session, status } = useSession()
  const sessionLoading = status === 'loading'

  const {
    register, handleSubmit, formState: { isValid, isSubmitting, errors }, control, setValue, watch, getValues,
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      autoTranslationLanguages: [],
      loopingWork: true,
      ...work,
    },
  })

  // Watch duration to display formatted time
  const durationValue = watch('duration')

  // Watch track to get audio URL
  const tracksValue = watch('tracks')
  const audioTrack = useMemo(() => {
    const tracks = tracksValue || []
    return tracks.length > 0 ? tracks[0] : null
  }, [tracksValue])

  // State to store audio track duration
  const [audioTrackDuration, setAudioTrackDuration] = useState<number | null>(null)

  // Get audio track duration from the audio URL
  useEffect(() => {
    const audioUrl = audioTrack?.audioUrl || work?.audioUrl
    if (!audioUrl) {
      setAudioTrackDuration(null)
      return undefined
    }

    const audio = new Audio(audioUrl)

    const handleLoadedMetadata = () => {
      if (audio.duration && Number.isFinite(audio.duration)) {
        setAudioTrackDuration(Math.round(audio.duration * 1000)) // Convert to milliseconds and round to nearest ms
      }
    }

    const handleError = () => {
      setAudioTrackDuration(null)
    }

    audio.addEventListener('loadedmetadata', handleLoadedMetadata)
    audio.addEventListener('error', handleError)

    audio.load()

    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata)
      audio.removeEventListener('error', handleError)
    }
  }, [audioTrack?.audioUrl, work?.audioUrl])

  // Calculate duration warnings
  const durationWarnings = useMemo(() => {
    const warnings: string[] = []

    if (audioTrackDuration !== null && durationValue) {
      const difference = Math.abs(durationValue - audioTrackDuration)
      // Warn if mismatch is more than 1 second
      if (difference > 1000) {
        warnings.push(`Warning: Entered work duration is ${durationValue > audioTrackDuration ? 'longer' : 'shorter'} than the audio track length ${convertMS(audioTrackDuration)} (${audioTrackDuration}ms).`)
      }
    }

    return warnings
  }, [durationValue, audioTrackDuration])

  const {
    fields: srtFields, append: appendSrt, remove: removeSrt, replace: replaceSrts,
  } = useFieldArray({
    control,
    keyName: 'fieldArrayId',
    name: 'srts',
  })
  const { fields: trackFields, remove: removeTrack } = useFieldArray({
    control,
    keyName: 'fieldArrayId',
    name: 'tracks',
  })

  const { data: availableLanguages } = useAvailableLanguages()

  // Create a stable signature of server SRTs to detect changes reliably
  const serverSrtsSignature = useMemo(() => {
    if (!work?.srts) return ''
    return work.srts.map((s) => `${s.id}:${s.shortDescriptor}`).join('|')
  }, [work?.srts])

  // Track the previous server SRT signature to detect server-side changes
  const prevServerSrtsSignatureRef = useRef<string | undefined>(undefined)

  // Sync form SRT fields when the server data changes (e.g. after auto-translation adds new SRTs)
  // This effect intentionally does NOT depend on form state to avoid undoing user edits
  useEffect(() => {
    if (!work?.srts) return

    const prevSignature = prevServerSrtsSignatureRef.current
    const isInitialLoad = prevSignature === undefined
    const serverDataChanged = !isInitialLoad && prevSignature !== serverSrtsSignature

    if (serverDataChanged) {
      replaceSrts(work.srts)
      setValue('autoTranslationLanguages', [])
    } else if (isInitialLoad && work.srts.length > 0) {
      work.srts.forEach(({ text }, index) => {
        setValue(`srts.${index}.text`, text)
      })
    }

    // Update ref to track current server signature for next comparison
    prevServerSrtsSignatureRef.current = serverSrtsSignature
  }, [serverSrtsSignature, work?.srts, replaceSrts, setValue])

  const { data: exhibitions, isLoading: isLoadingExhibitions, isError } = useExhibitions(true)
  const { data: features, isLoading: isLoadingTenantFeatures } = useTenantFeatures()
  const isLoading = isLoadingExhibitions || isLoadingTenantFeatures


  const watchedSrts = watch('srts') || []
  const hasMissingDescriptors = watchedSrts.some((srt) => !srt?.shortDescriptor?.trim() || !srt?.longDescriptor?.trim())
  const hasEmptySrts = watchedSrts.some((srt) => srt && (!srt.text || srt.text.trim() === ''))
  const [srtParseErrors, setSrtParseErrors] = useState<Record<number, string>>({})
  const hasSrtParseErrors = Object.keys(srtParseErrors).length > 0
  const extendedIsValid = isValid && !isError && !hasEmptySrts && !hasMissingDescriptors && !hasSrtParseErrors

  // Debounced toast notifications to prevent visual clutter during typing
  const debouncedSuccessToast = useDebouncedCallback(() => {
    toast.success('Captions saved.')
  }, 1000)

  const debouncedErrorToast = useDebouncedCallback((error: string) => {
    toast.error(error)
  }, 1000)

  const parseAndUpdateSRTField = useCallback((srtString: string, index: number) => {
    setValue(`srts.${index}.text`, srtString)

    if (!srtString.trim()) {
      setSrtParseErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors[index]
        return newErrors
      })
      return
    }

    const lastCaptionTime = getLastCaptionTime(srtString)
    if (lastCaptionTime === null) {
      const message = 'Invalid SRT format. Each entry needs a sequence number, timestamp (HH:MM:SS,mmm --> HH:MM:SS,mmm), and caption text.'
      setSrtParseErrors((prev) => ({
        ...prev,
        [index]: message,
      }))
      debouncedErrorToast('Error parsing SRT.')
      return
    }

    // Only set work duration to last caption time if no timestamps have been
    // submitted from player or work.duration is the default 1hr placeholder.
    if ((!work?.duration || work?.duration === 3600000) && !work?.lastTimestamp) {
      setValue('duration', lastCaptionTime + 2000)
    }
    setSrtParseErrors((prev) => {
      const newErrors = { ...prev }
      delete newErrors[index]
      return newErrors
    })
    debouncedSuccessToast()
  }, [setValue, work, debouncedSuccessToast, debouncedErrorToast])

  const onChangeSrtInput = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const file = e.target.files[0]
    const reader = new FileReader()
    reader.readAsText(file)
    reader.onload = () => {
      // make sure our SRT parses
      const srtString = String(reader.result)
      parseAndUpdateSRTField(srtString, index)
    }
    reader.onerror = () => {
      toast.error(`Error loading SRT: ${reader.error}`)
    }
  }
  const submitData = async (data: Work & {srts: SRT[], tracks: Track[], autoTranslationLanguages: LanguageCode[]}) => {
    const { srts } = data

    // Lang codes selected for translation which have a matching srt upload file are ignored
    const nonOverriddenAutoLangs = data.autoTranslationLanguages?.filter((code) => !srts.some(({ shortDescriptor }) => shortDescriptor === code))
    const autoTranslationLanguages = nonOverriddenAutoLangs?.map((code) => availableLanguages?.find((lang) => lang.value === code)) ?? []
    if (work) {
      const promise = updateWork({ id: work.id, work: data, autoTranslationLanguages })
      toast.promise(promise, {
        loading: `Updating work${autoTranslationLanguages.length ? ' and translating captions...' : ''}`,
        success: ({ error }) => { if (error) throw error.message; return 'Work updated.' },
        error: (err) => err,
      })
      const { error } = await promise
      if (!error) await mutateWork?.()
    } else {
      const promise = createWork({ work: data, autoTranslationLanguages })
      toast.promise(promise, {
        loading: `Creating work${autoTranslationLanguages.length ? ' and translating captions...' : ''}`,
        success: ({ error }) => { if (error) throw error.message; return 'Work created.' },
        error: (message) => message,
      })
      const { error } = await promise
      if (!error) await Router.push('/admin')
    }
  }

  const handleClickDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this work?')) {
      return
    }
    const { error } = await deleteWork(work.id)
    if (error) {
      toast.error(error.message)
    } else {
      toast.success('Work deleted.')
      await Router.push('/admin')
    }
  }
  const {
    setAdvancedWorkSettingsShowing,
    advancedWorkSettingsShowing,
  } = useContext(AdminUiContext)

  if (isLoading) {
    return (
      <p>Loading...</p>
    )
  }

  if (isError) {
    return (
      <p className="text-black font-semibold">
        Failed to load required data. Please refresh the page and try again.
      </p>
    )
  }

  return (
    <div>
      <form onSubmit={handleSubmit(submitData)}>
        <div className="form-header">
          <div>
            <h1>{`${!work ? 'New ' : ''}Work${work ? ' Settings' : ''}`}</h1>
            {work && (<a href={`/admin/work/${work.id}/control`}>[controls]</a>)}
            {work && (<a href={`/work/${work.id}?includeInactive=true`} target="_blank" rel="noreferrer">[guest view]</a>)}
          </div>
          <a href="https://docs.accesskit.cc/reference/work" target="_blank" rel="noreferrer">Help</a>
        </div>
        <label htmlFor="title">
          Title
          {isQuillEmpty(watch('title')) && <span className="text-red-600"> *</span>}
          <WorkTitleHelp />
          <Controller
            name="title"
            defaultValue=""
            control={control}
            rules={{ validate: (v) => !isQuillEmpty(v) || 'Title is required' }}
            render={({ field }) => (
              <ReactQuill
                theme="snow"
                placeholder="The Clock, 1999"
                {...field}
                modules={{ toolbar: ['bold', 'italic', 'underline'] }}
              />
            )}
          />
          {isQuillEmpty(watch('title')) && <p className="text-sm text-red-600 mb-2">Title is required</p>}
          {!isQuillEmpty(watch('title')) && errors.title?.message && <p className="text-sm text-red-600 mb-2">{errors.title.message}</p>}
        </label>
        <label htmlFor="exhibitionId">
          Exhibition
          <select disabled={isLoading || isError} {...register('exhibitionId')}>
            <option value="">(none)</option>
            {exhibitions?.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
        </label>
        <div className="my-4" />
        <div className="srt-uploads-form">
          {srtFields.map((srt, index) => {
            const currentSrt = watchedSrts?.[index] as SRT | undefined
            return (
              <div className="srt-uploads-form-item" key={srt.id}>
                <label htmlFor={`srts.${index}.file`}>
                  SRT File Upload
                  {(!watchedSrts[index]?.text || !watchedSrts[index]?.text?.trim()) && <span className="text-red-600"> *</span>}
                  <textarea
                    rows={12}
                    {...register(`srts.${index}.text`)}
                    onChange={(e) => parseAndUpdateSRTField(e.target.value, index)}
                  />
                  <input
                    type="file"
                    key={srt.id}
                    onChange={(file) => onChangeSrtInput(file, index)}
                    accept=".srt"
                  />
                  {(!watchedSrts[index]?.text || !watchedSrts[index]?.text?.trim()) && (
                    <p className="text-sm text-red-600 mb-2">SRT is empty. Please add content or remove it.</p>
                  )}
                  {srtParseErrors[index] && (
                    <>
                      <p className="text-sm text-red-600 mb-2">{srtParseErrors[index]}</p>
                    </>
                  )}
                </label>
                <div className="srt-uploads-form-descriptors">
                  <label htmlFor={`srts.${index}.shortDescriptor`}>
                    Language Display Code (e.g. &quot;EN&quot; or &quot;DE&quot;)
                    {!currentSrt.shortDescriptor?.trim() || errors.srts?.[index]?.shortDescriptor ? <span className="text-red-600"> *</span> : null}
                    <WorkSRTShortDescriptorHelp />
                    <input
                      type="text"
                      placeholder="EN-US"
                      maxLength={15}
                      {...register(`srts.${index}.shortDescriptor`, {
                        validate: (desc: string) => {
                          if (!desc?.trim()) return 'Language code is required'
                          if (desc.length > 15) return 'Language code must be 15 characters or less'
                          return undefined
                        },
                      })}
                    />
                    {errors.srts?.[index]?.shortDescriptor && (
                      <p className="text-sm text-red-600">{errors.srts[index].shortDescriptor.message}</p>
                    )}
                  </label>
                  <label htmlFor={`srts.${index}.longDescriptor`}>
                    SRT Language Descriptor (Long)
                    {!currentSrt.longDescriptor?.trim() && <span className="text-red-600"> *</span>}
                    <input
                      type="text"
                      placeholder="English (US)"
                      {...register(`srts.${index}.longDescriptor`, {
                        validate: (desc: string) => {
                          if (!desc?.trim()) return 'Long description is required'
                          return undefined
                        },
                      })}
                    />
                    {errors.srts?.[index]?.longDescriptor && (
                      <p className="text-sm text-red-600">{errors.srts[index].longDescriptor.message}</p>
                    )}
                  </label>
                  <button
                    type="button"
                    className="text-red-600 w-full"
                    onClick={async () => {
                      const srtId = watch(`srts.${index}.id`)
                      if (srtId) {
                        if (!window.confirm('Are you sure you want to delete this caption track?')) {
                          return
                        }
                        const res = await deleteSrt(srtId)
                        if (res.error) {
                          toast.error(res.error.message)
                          return
                        }
                        toast.success('Caption track deleted.')
                      }
                      removeSrt(index)
                      setSrtParseErrors((prev) => {
                        const next: Record<number, string> = {}
                        Object.entries(prev).forEach(([k, v]) => {
                          const i = Number(k)
                          if (i < index) next[i] = v
                          else if (i > index) next[i - 1] = v
                        })
                        return next
                      })
                    }}
                  >
                    Delete
                  </button>
                </div>
                {/* <p>{work?.srtParseError}</p> */}
              </div>
            )
          })}
          <p>
            {
              srtFields.length >= 1 && !features.includes(Feature.MULTILINGUAL_CAPTIONS)
                ? 'Your plan only includes one caption track per work.'
                : (
                  <button
                    type="button"
                    onClick={() => appendSrt({
                      shortDescriptor: '', longDescriptor: '', text: '', filename: '',
                    } as SRT)}
                  >
                    Add Additional Captions
                  </button>
                )
            }
          </p>
          {srtFields.length > 0 && (
            <div className="translations-editor">
              <label htmlFor="autoTranslationLanguages">
                Automatic Translation Languages
                {' '}
                <AutoTranslationHelper />
                <br />
                {features.includes(Feature.MULTILINGUAL_CAPTIONS) && `${watch('autoTranslationLanguages')?.map((value) => availableLanguages?.find((opt) => opt.value === value)?.label).filter(Boolean).join(', ')}`}
                {!features.includes(Feature.MULTILINGUAL_CAPTIONS) && <em>(not included in your current plan)</em>}
                <Controller
                  name="autoTranslationLanguages"
                  control={control}
                  rules={{
                    validate: (opts) => ((opts?.length > 0 && (opts.length > 5 - srtFields.length)) ? 'Too many multilingual translations selected' : undefined),
                  }}
                  render={({ field: { onChange, value, ref } }) => (
                    <select
                      disabled={!features.includes(Feature.MULTILINGUAL_CAPTIONS)}
                      multiple
                      ref={ref}
                      value={value || []}
                      onChange={(e) => {
                        const selectedOptions = Array.from(e.target.selectedOptions, (option) => option.value)
                        onChange(selectedOptions)
                      }}
                    >
                      {availableLanguages?.map((opt) => (
                        <option key={`auto-lang-option-${opt.value}`} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  )}
                />
                <ErrorMessage
                  errors={errors}
                  name="autoTranslationLanguages"
                  render={({ message }) => <p className="text-sm text-red-600">{message}</p>}
                />
              </label>
              <div>
                <input
                  type="submit"
                  value="Add Auto-Translated Captions"
                  disabled={!extendedIsValid || isSubmitting}
                  className={`px-4 py-2 mr-4 text-base border cursor-pointer ${
                    !extendedIsValid || isSubmitting
                      ? 'bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed opacity-60'
                      : 'bg-gray-200 text-gray-700 border-gray-400'
                  }`}
                />
              </div>
            </div>
          )}
        </div>
        <label htmlFor="audio-upload" className="block my-4">
          Audio Track
          {' '}
          {!features.includes(Feature.AUDIO) && <em>Not included in current plan</em>}
          <WorkAudioUploaderHelper />
        </label>
        {
          trackFields
            ? trackFields.map(
              (t, index) => (
                <TrackEditor
                  key={t.fieldArrayId}
                  track={getValues('tracks')[index]}
                  onDeleteTrackSuccess={() => { removeTrack(index) }}
                />
              )
            )
            : <p>Work does not have audio track(s).</p>
        }
        <AudioUploader
          label="tracks"
          register={register}
          work={work}
          onUploadSuccess={(res) => {
            logger.info({ track: res.track }, 'Audio track uploaded')
            setValue('tracks', [res.track])
            mutateWork()
            // setValue('audioUrl', res.publicUrl)
          }}
          disabled={!features.includes(Feature.AUDIO)}
        />
        <label htmlFor="duration">
          Duration (ms)
          <WorkDurationHelp />
          <div className="flex items-center gap-2">
            <input
              type="number"
              placeholder="3600000"
              className="!w-1/2 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              {...register('duration')}
            />
            <span className="text-sm text-gray-600">
              {convertMS(durationValue)}
            </span>
          </div>
          <p>{errors.duration?.message}</p>
          {durationWarnings.map((warning) => (
            <p key={warning} className="text-sm text-amber-600">{warning}</p>
          ))}
        </label>
        <label htmlFor="loopingWork" className="flex gap-2">
          <input
            type="checkbox"
            disabled={!features.includes(Feature.NON_LOOPING_WORKS)}
            {...register('loopingWork')}
          />
          Looping work?
          {!features.includes(Feature.NON_LOOPING_WORKS) && <em> Not editable in current plan.</em>}
        </label>
        <label htmlFor="isActive" className="flex gap-2">
          <input type="checkbox" {...register('isActive')} />
          Published?
          <WorkPublishedHelp />
        </label>
        <button
          className="advanced-settings-toggle"
          type="button"
          onClick={() => { setAdvancedWorkSettingsShowing(!advancedWorkSettingsShowing) }}
        >
          {advancedWorkSettingsShowing ? 'Hide ' : 'Show '}
          Advanced Settings
        </button>
        <div style={{ display: advancedWorkSettingsShowing ? 'block' : 'none' }}>
          <label htmlFor="nudge">
            Nudge (ms)
            <input type="number" {...register('nudge')} placeholder="0" />
          </label>
          <label htmlFor="dilation">
            Dilation (unitless)
            <WorkDilationHelp />
            <input step="0.000001" type="number" {...register('dilation')} placeholder="1" />
          </label>
          <label htmlFor="backlinkUrl">
            Backlink URL
            <WorkBacklinkHelp />
            <input
              placeholder="https://example.com/guide/the-work"
              type="text"
              {...register('backlinkUrl')}
            />
            <p>{errors.backlinkUrl?.message}</p>
          </label>
          <label htmlFor="audioUrl">
            Alternative Audio URL
            <WorkAudioUrlHelp />
            {!features.includes(Feature.AUDIO) && <em> Not included in current plan.</em>}
            <input
              disabled={!features.includes(Feature.AUDIO)}
              placeholder="https://s3.amazonaws.com/example/todo-add-file-upload.mp3"
              type="text"
              {...register('audioUrl')}
            />
          </label>

        </div>
        <div className="form-submit">
          <input
            type="submit"
            value={`${work ? 'Update' : 'Create'} Work`}
            disabled={!extendedIsValid || isSubmitting}
            className={`px-8 py-4 mr-4 text-base border cursor-pointer ${
              !extendedIsValid || isSubmitting
                ? 'bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed opacity-60'
                : 'bg-gray-200 text-gray-700 border-gray-400'
            }`}
          />
          {work && <button className="delete" type="button" onClick={handleClickDelete}>Delete Work</button>}
          <Link href="/admin">
            or Cancel
          </Link>
        </div>
      </form>
      {
        work && (
          <>
            <h5>Read-Only Data</h5>
            <p>Last timestamp:</p>
            <pre>{formatTimestamp(Number(work.lastTimestamp))}</pre>
            {!work?.loopingWork && (
            <button
              type="button"
              onClick={async () => {
                await resetTimestamp(work.id)
                mutateWork()
                toast.success('Timestamp reset.')
              }}
            >
              Reset Timestamp
            </button>
            )}
            <p>ID:</p>
            <p>
              <span style={{ fontFamily: 'monospace' }}>{work.id}</span>
              {' '}
              <a href={`/work/${work.id}`}>Guest View</a>
            </p>
            <p>QR Code:</p>
            <DownloadableQRCode
              id={work.id}
              title={titleToFilename(getQuillPlaintext(work.title))}
              value={`${getOrigin(session && !sessionLoading && session.user.tenantData?.tenantAlias)}/work/${work.id}`}
            />
          </>
        )
      }
    </div>
  )
}
